import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Star, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';
import CigarImage from '../components/CigarImage';

// Helper function to display cigar name without duplicating brand
const formatCigarName = (brand, name) => {
  if (!name || name.toLowerCase() === brand?.toLowerCase()) {
    return brand || 'Unknown';
  }
  return `${brand} ${name}`;
};

const MyRatingsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [ratings, setRatings] = useState([]);
  const [cigars, setCigars] = useState({});
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('latest'); // 'latest', 'highest', 'lowest'
  const [showSortMenu, setShowSortMenu] = useState(false);

  useEffect(() => {
    if (token) {
      fetchMyRatings();
    }
  }, [token]);

  const fetchMyRatings = async () => {
    try {
      const res = await axios.get(`${API}/ratings/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRatings(res.data);
      
      // Fetch cigar details in a single batch request
      const uniqueCigarIds = [...new Set(res.data.map(r => r.cigar_id))];
      
      if (uniqueCigarIds.length > 0) {
        try {
          const batchRes = await axios.post(`${API}/cigars/batch`, uniqueCigarIds, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const cigarDetails = {};
          for (const cigar of batchRes.data) {
            cigarDetails[cigar.id] = cigar;
          }
          setCigars(cigarDetails);
        } catch (err) {
          console.error('Failed to fetch cigars batch');
        }
      }
    } catch (error) {
      console.error('Failed to fetch ratings');
    } finally {
      setLoading(false);
    }
  };

  // Sort ratings based on selected option
  const sortedRatings = useMemo(() => {
    const sorted = [...ratings];
    switch (sortBy) {
      case 'highest':
        return sorted.sort((a, b) => b.rating - a.rating);
      case 'lowest':
        return sorted.sort((a, b) => a.rating - b.rating);
      case 'latest':
      default:
        return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
  }, [ratings, sortBy]);

  const sortOptions = [
    { value: 'latest', label: 'Latest' },
    { value: 'highest', label: 'Highest Score' },
    { value: 'lowest', label: 'Lowest Score' }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/70 mb-4">Please sign in to view your ratings</p>
          <Button onClick={() => navigate('/auth')} className="bg-amber-600">Sign In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex flex-col">
      {/* Header */}
      <div className="flex items-center p-4 border-b border-white/10">
        <button onClick={() => navigate(-1)} className="text-white mr-4">
          <X size={24} />
        </button>
        <h1 className="playfair text-xl text-white flex-1 text-center pr-8">My Ratings</h1>
      </div>

      {/* Sort Selector */}
      {ratings.length > 0 && (
        <div className="px-4 py-3 border-b border-white/5">
          <div className="relative inline-block">
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 px-4 py-2 rounded-lg text-white/80 text-sm transition-colors"
            >
              <span>Sort: {sortOptions.find(o => o.value === sortBy)?.label}</span>
              <ChevronDown size={16} className={`transition-transform ${showSortMenu ? 'rotate-180' : ''}`} />
            </button>
            
            {showSortMenu && (
              <>
                {/* Backdrop to close menu */}
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowSortMenu(false)}
                />
                
                {/* Dropdown menu */}
                <div className="absolute left-0 mt-2 w-40 bg-[#1a1a1c] border border-white/10 rounded-lg shadow-xl z-20 overflow-hidden">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSortBy(option.value);
                        setShowSortMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        sortBy === option.value 
                          ? 'bg-amber-500/20 text-amber-500' 
                          : 'text-white/70 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Ratings List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-24">
        {loading ? (
          <div className="text-center text-white/50 py-8">Loading...</div>
        ) : ratings.length === 0 ? (
          <div className="text-center text-white/50 py-8">You haven&apos;t rated any cigars yet.</div>
        ) : (
          sortedRatings.map((rating) => {
            const cigar = cigars[rating.cigar_id];
            return (
              <div
                key={rating.id}
                onClick={() => navigate(`/cigar/${rating.cigar_id}`)}
                className="bg-white/5 rounded-xl p-4 cursor-pointer hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-4">
                  {/* Cigar Image */}
                  <div className="w-16 h-16 bg-white/5 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                    <CigarImage src={cigar?.image_url} alt={cigar?.name} />
                  </div>
                  
                  {/* Cigar Info */}
                  <div className="flex-1 min-w-0">
                    {cigar ? (
                      <>
                        <p className="text-xs text-white/50">{cigar.brand}</p>
                        <p className="text-white font-semibold truncate">{formatCigarName(cigar.brand, cigar.name)}</p>
                      </>
                    ) : (
                      <p className="text-white/50">Loading cigar info...</p>
                    )}
                    <p className="text-white/40 text-sm mt-1">Rated on {formatDate(rating.created_at)}</p>
                  </div>
                  
                  {/* User's Rating */}
                  <div className="flex items-center gap-1 bg-amber-500/20 px-3 py-2 rounded-lg">
                    <Star className="text-amber-500 fill-amber-500" size={18} />
                    <span className="text-amber-500 font-bold text-lg">{rating.rating.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      
      <BottomNav />
    </div>
  );
};

export default MyRatingsPage;
